import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Inventory } from '../interfaces/inventory';

export interface FlowProduct {
  id: string;
  name: string;
  image: string | null;
  price: number;
}

export interface FlowExtra {
  id: string;
  name: string;
  price: number;
}

// Contexto compartido de la tienda para toda la app: url publica (links a
// ordenes del back office), sedes (para mapear selecciones de flows) y un
// cache de productos/extras referenciados por las respuestas de flows.
@Injectable({
  providedIn: 'root',
})
export class StoreContextService {
  inventories: Inventory[] = [];
  private urlStore: string | null = null;
  private storeInfoRequest: Promise<void> | null = null;
  private products = new Map<string, FlowProduct>();
  private extras = new Map<string, FlowExtra>();
  private resolvedIds = new Set<string>();

  constructor(private api: ApiService) {}

  async getStoreUrl(): Promise<string | null> {
    if (!this.storeInfoRequest) {
      this.storeInfoRequest = this.api
        .getStoreInfo()
        .then(info => {
          this.urlStore = info?.urlStore
            ? info.urlStore.replace(/\/+$/, '')
            : null;
        })
        .catch(() => {
          // Permite reintentar en la proxima consulta si fallo la llamada
          this.storeInfoRequest = null;
        });
    }
    await this.storeInfoRequest;
    return this.urlStore;
  }

  getSaleUrl(saleId: number): string | null {
    if (!this.urlStore) return null;
    return `${this.urlStore}/admin/orders/detail/${saleId}?isGlobal=false`;
  }

  getInventoryName(inventoryId: number): string | null {
    return this.inventories.find(inv => inv.id === inventoryId)?.name ?? null;
  }

  getProduct(id: string): FlowProduct | null {
    return this.products.get(id) ?? null;
  }

  getExtra(id: string): FlowExtra | null {
    return this.extras.get(id) ?? null;
  }

  // Resuelve en lote los ids aun no consultados; los que el backend no
  // devuelva quedan marcados para no re-consultarlos por cada mensaje
  async resolveFlowEntities(
    productIds: string[],
    extraIds: string[]
  ): Promise<void> {
    const pendingProducts = productIds.filter(id => !this.resolvedIds.has(id));
    const pendingExtras = extraIds.filter(id => !this.resolvedIds.has(id));
    if (pendingProducts.length === 0 && pendingExtras.length === 0) return;

    try {
      const res = await this.api.getFlowEntities(
        pendingProducts,
        pendingExtras
      );
      [...pendingProducts, ...pendingExtras].forEach(id =>
        this.resolvedIds.add(id)
      );
      res.products?.forEach(product => this.products.set(product.id, product));
      res.extras?.forEach(extra => this.extras.set(extra.id, extra));
    } catch (error) {
      console.error('Error resolving flow entities: ', error);
    }
  }
}
